// =====================================================
// storage.ts
// Point d'entrée UNIQUE pour la langue, appelé par tous les
// composants de l'app — web et mobile indifféremment.
//
// Mobile : écrit/lit directement via AsyncStorage.
// Web    : écrit via AsyncStorage (comportement existant, inchangé)
//          ET synchronise vers language.ts (localStorage +
//          notification au service worker), pour que le service
//          worker connaisse la langue courante sans dépendre
//          d'AsyncStorage (inaccessible dans son contexte).
//
// Les composants n'appellent jamais language.ts directement —
// uniquement saveLanguage() / getSavedLanguage() ci-dessous.
// =====================================================

import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';

const LANGUAGE_KEY = 'harmonia_language';

// ── Synchronisation vers le web (localStorage + service worker) ──────────────
// Fonction interne — jamais appelée directement par les composants.
// Ses erreurs sont interceptées ici pour ne jamais faire échouer saveLanguage.
async function syncLanguageToWeb(language: string): Promise<void> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { saveLanguageWeb } = require('./language');
    await saveLanguageWeb(language);
  } catch (error) {
    console.warn('Erreur lors de la synchronisation de la langue vers le web :', error);
  }
}

// ── Sauvegarder la langue (web + mobile) ──────────────────────────────────────
export async function saveLanguage(language: string): Promise<void> {
  try {
    await AsyncStorage.setItem(LANGUAGE_KEY, language);
  } catch (error) {
    console.warn('Erreur lors de la sauvegarde de la langue :', error);
  }

  if (Platform.OS === 'web') {
    await syncLanguageToWeb(language);
  }
}

// ── Récupérer la langue sauvegardée (web + mobile) ─────────────────────────────
export async function getSavedLanguage(): Promise<string | null> {
  if (Platform.OS === 'web') {
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const { getSavedLanguageWeb } = require('./language');
      return getSavedLanguageWeb();
    } catch (error) {
      console.warn('Erreur lors de la récupération de la langue (web) :', error);
      return null;
    }
  }

  try {
    return await AsyncStorage.getItem(LANGUAGE_KEY);
  } catch (error) {
    console.warn('Erreur lors de la récupération de la langue :', error);
    return null;
  }
}
